import React, { useState, useEffect, useCallback } from 'react';
import { FileSpreadsheet, Download, Filter, Loader2, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

import { tripsApi } from '@/services/tripsApi';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';

// Opções de agrupamento disponíveis no backend (reports.py _GROUP_BY_ALLOWED).
const GROUP_BY_OPTIONS = [
  { value: 'period', label: 'Por período (mês)' },
  { value: 'equipment', label: 'Por equipamento' },
  { value: 'area', label: 'Por área (destino)' },
];

// Formata número decimal (ou null) para exibição pt-BR. Nunca recalcula — só formata o
// valor já pronto que chegou do backend. INV-UI-TRAIL-001.
const fmtNum = (v, decimals = 1) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });

const fmtInt = (v) =>
  v == null ? '—' : Number(v).toLocaleString('pt-BR', { maximumFractionDigits: 0 });

/**
 * ReportsPage — Relatórios agregados de viagens fechadas.
 *
 * Filtros: período (start_after / start_before), equipamento, agrupamento.
 * A página SOMENTE EXIBE os números vindos do backend (/aggregate).
 * Nenhum cálculo é feito no cliente (INV-UI-TRAIL-001).
 * RBAC é 100% server-side (JWT); o cliente não envia o conjunto visível.
 */
function ReportsPage() {
  // Filtros de data (ISO date strings YYYY-MM-DD ou '')
  const [startAfter, setStartAfter] = useState('');
  const [startBefore, setStartBefore] = useState('');
  // Equipamento selecionado ('' = todos os visíveis pelo RBAC do backend)
  const [equipmentId, setEquipmentId] = useState('');
  // Agrupamento
  const [groupBy, setGroupBy] = useState('period');

  // Lista de equipamentos distintos derivada do listConsole (RBAC já filtra no backend).
  const [equipments, setEquipments] = useState([]);

  // Resultado do /aggregate
  const [groups, setGroups] = useState([]);
  const [totais, setTotais] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState(false);
  const [exporting, setExporting] = useState(false);

  // Carrega a lista de equipamentos distintos a partir do console (RBAC do JWT).
  // Usa listConsole sem filtros — sem endpoint novo (conforme plano, seção <interfaces>).
  useEffect(() => {
    tripsApi.listConsole().then((res) => {
      const trips = res.data || [];
      // Deriva equipamentos únicos visíveis (RBAC já aplicado pelo backend).
      const seen = new Set();
      const list = [];
      for (const t of trips) {
        if (t.equipment_id && !seen.has(t.equipment_id)) {
          seen.add(t.equipment_id);
          list.push({
            id: t.equipment_id,
            label: [t.equipment_name, t.equipment_tag ? `(${t.equipment_tag})` : '']
              .filter(Boolean).join(' ') || t.equipment_id,
          });
        }
      }
      setEquipments(list);
    }).catch((err) => {
      console.error('[Reports] erro ao carregar equipamentos:', err);
    });
  }, []);

  // Busca o relatório agregado com os filtros atuais.
  const fetchAggregate = useCallback(async () => {
    setLoadError(false);
    setLoading(true);
    setGroups([]);
    setTotais(null);

    // Monta params — só envia equipment_id se truthy (filtro opcional).
    // RBAC é do backend via JWT; o cliente NÃO envia o conjunto visível.
    const params = { group_by: groupBy };
    if (startAfter) params.start_after = startAfter;
    if (startBefore) params.start_before = startBefore;
    if (equipmentId) params.equipment_id = equipmentId;

    try {
      const res = await tripsApi.getAggregate(params);
      const data = res.data || {};
      setGroups(data.groups || []);
      setTotais(data.totais || null);
    } catch (err) {
      console.error('[Reports] erro ao carregar aggregate:', err);
      setLoadError(true);
      toast.error('Erro ao carregar o relatório. Verifique os filtros e tente novamente.');
    } finally {
      setLoading(false);
    }
  }, [startAfter, startBefore, equipmentId, groupBy]);

  // Carrega na montagem e quando filtros mudam (via "Aplicar").
  // Carga inicial sem filtro para mostrar o panorama geral.
  useEffect(() => {
    fetchAggregate();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // somente na montagem — re-fetch acionado pelo botão "Aplicar"

  // Aciona o download CSV com os filtros atuais (sem group_by — o CSV é por viagem).
  // Mantém o JWT global do axios (sem token na URL). Espelha downloadAttachment.
  // INV-UI-TRAIL-001: o XLSX é produzido 100% pelo backend.
  const handleExportXlsx = async () => {
    setExporting(true);
    const params = {};
    if (startAfter) params.start_after = startAfter;
    if (startBefore) params.start_before = startBefore;
    if (equipmentId) params.equipment_id = equipmentId;

    try {
      const res = await tripsApi.exportXlsx(params);
      const url = URL.createObjectURL(
        new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        }),
      );
      const a = document.createElement('a');
      a.href = url;
      a.download = 'relatorio_viagens.xlsx';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('XLSX exportado com sucesso.');
    } catch (err) {
      console.error('[Reports] erro ao exportar XLSX:', err);
      toast.error('Erro ao exportar o XLSX. Tente novamente.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <div className="page-container space-y-6" data-testid="reports-page">
      {/* Cabeçalho */}
      <div className="pl-12 md:pl-0">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <FileSpreadsheet className="w-6 h-6 text-primary" />
          Relatórios
        </h1>
        <p className="text-sm text-muted-foreground">
          Consolidados de viagens fechadas por período, equipamento ou área de destino.
        </p>
      </div>

      {/* Card de filtros */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Filter className="w-4 h-4 text-primary" />
            Filtros
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 items-end">
            {/* Período — data de início (start_after) */}
            <div className="space-y-2">
              <Label>De (início da viagem)</Label>
              <input
                type="date"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={startAfter}
                onChange={(e) => setStartAfter(e.target.value)}
              />
            </div>

            {/* Período — data final (start_before) */}
            <div className="space-y-2">
              <Label>Até (início da viagem)</Label>
              <input
                type="date"
                className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm transition-colors placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring"
                value={startBefore}
                onChange={(e) => setStartBefore(e.target.value)}
              />
            </div>

            {/* Equipamento */}
            <div className="space-y-2">
              <Label>Equipamento</Label>
              <Select
                value={equipmentId || 'all'}
                onValueChange={(v) => setEquipmentId(v === 'all' ? '' : v)}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Todos os equipamentos" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Todos os equipamentos</SelectItem>
                  {equipments.map((eq) => (
                    <SelectItem key={eq.id} value={eq.id}>
                      {eq.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Agrupamento */}
            <div className="space-y-2">
              <Label>Agrupar por</Label>
              <Select value={groupBy} onValueChange={setGroupBy}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {GROUP_BY_OPTIONS.map((opt) => (
                    <SelectItem key={opt.value} value={opt.value}>
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Ações: Aplicar + Exportar XLSX */}
          <div className="flex flex-wrap gap-3 mt-4">
            <Button
              onClick={fetchAggregate}
              disabled={loading}
              className="gap-2"
            >
              {loading
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Filter className="w-4 h-4" />}
              Aplicar
            </Button>

            <Button
              variant="outline"
              onClick={handleExportXlsx}
              disabled={exporting || loading}
              className="gap-2"
              title="Exportar viagens fechadas (mesmos filtros, sem agrupamento) como XLSX"
            >
              {exporting
                ? <Loader2 className="w-4 h-4 animate-spin" />
                : <Download className="w-4 h-4" />}
              Exportar XLSX
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Card da tabela de consolidados */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2">
            <FileSpreadsheet className="w-4 h-4 text-primary" />
            Consolidado
            {groupBy && (
              <span className="text-xs font-normal text-muted-foreground ml-1">
                — {GROUP_BY_OPTIONS.find((o) => o.value === groupBy)?.label || groupBy}
              </span>
            )}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex items-center justify-center py-12 text-muted-foreground">
              <Loader2 className="w-4 h-4 animate-spin mr-2" />
              Carregando relatório…
            </div>
          ) : loadError ? (
            <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
              <AlertTriangle className="w-5 h-5 text-destructive" />
              <p className="text-sm">Erro ao carregar o relatório.</p>
              <Button variant="outline" size="sm" onClick={fetchAggregate}>
                Tentar novamente
              </Button>
            </div>
          ) : groups.length === 0 ? (
            <p className="text-center text-muted-foreground py-12 text-sm">
              Nenhum dado encontrado para os filtros selecionados.
            </p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Grupo</TableHead>
                  <TableHead className="text-right">Nº viagens</TableHead>
                  <TableHead className="text-right">Volume (t)</TableHead>
                  <TableHead className="text-right">Consumo (L)</TableHead>
                  <TableHead className="text-right">Duração total (h)</TableHead>
                  <TableHead className="text-right">Duração média (h)</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {/* Linhas de grupos — valores já calculados pelo backend; aqui só formatamos para exibição.
                    INV-UI-TRAIL-001: NENHUMA soma/agregação ocorre no cliente. */}
                {groups.map((g) => (
                  <TableRow key={g.key}>
                    <TableCell className="font-medium">{g.label || g.key}</TableCell>
                    <TableCell className="text-right">{fmtInt(g.num_viagens)}</TableCell>
                    <TableCell className="text-right">{fmtNum(g.volume_t_total)}</TableCell>
                    <TableCell className="text-right">{fmtNum(g.consumo_l_total, 0)}</TableCell>
                    <TableCell className="text-right">{fmtNum(g.duracao_total_h)}</TableCell>
                    <TableCell className="text-right">{fmtNum(g.duracao_media_h)}</TableCell>
                  </TableRow>
                ))}

                {/* Rodapé de totais — valores do backend (totais calculados server-side).
                    INV-UI-TRAIL-001: não somamos grupos; exibimos totais.num_viagens do backend. */}
                {totais && (
                  <TableRow className="border-t-2 border-border font-semibold bg-muted/30">
                    <TableCell>Total</TableCell>
                    <TableCell className="text-right">{fmtInt(totais.num_viagens)}</TableCell>
                    <TableCell className="text-right">{fmtNum(totais.volume_t_total)}</TableCell>
                    <TableCell className="text-right">{fmtNum(totais.consumo_l_total, 0)}</TableCell>
                    <TableCell className="text-right">{fmtNum(totais.duracao_total_h)}</TableCell>
                    <TableCell className="text-right">{fmtNum(totais.duracao_media_h)}</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          <p className="text-xs text-muted-foreground mt-3">
            Somente viagens com status <span className="font-mono">fechada</span> são incluídas.
            Métricas calculadas pelo backend (INV-UI-TRAIL-001). RBAC aplicado via JWT — você vê
            apenas os equipamentos atribuídos ao seu perfil.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

export default ReportsPage;
